import * as React from 'react'
import { createPortal } from 'react-dom'

import { ActivityModel } from '@/app/models'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

export type VehicleActivityMenuState = {
  activity: ActivityModel
  x: number
  y: number
}

type Props = VehicleActivityMenuState & {
  canEdit: boolean
  onEdit: (activity: ActivityModel) => void
  onClose: () => void
}

// WPF ActivityItem.CanChangeActivityCommandExecute (kontekst pojazdu): bez tarczek; id = 0 to blok
// bez wiersza w VehicleActivities (luka z karty kierowcy, aktywność „nieskończona”).
export const canEditVehicleActivity = (activity: ActivityModel) =>
  activity.id > 0 && activity.driverTachographChartId == null

const VehicleActivityContextMenu: React.FC<Props> = ({
  activity,
  x,
  y,
  canEdit,
  onEdit,
  onClose,
}) => {
  const { t } = useLocalize()
  const menuRef = React.useRef<HTMLDivElement>(null)
  const [position, setPosition] = React.useState({ x, y })

  React.useLayoutEffect(() => {
    const rect = menuRef.current?.getBoundingClientRect()
    if (!rect) return
    setPosition({
      x: Math.min(x, window.innerWidth - rect.width - 5),
      y: Math.min(y, window.innerHeight - rect.height - 5),
    })
  }, [x, y])

  React.useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) onClose()
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', handleMouseDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleMouseDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [onClose])

  const disabled = !canEdit || !canEditVehicleActivity(activity)

  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      className="tw-fixed tw-z-[99999] tw-min-w-[180px] tw-rounded tw-bg-white tw-py-1 tw-shadow-lg"
      style={{ top: position.y, left: position.x }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <button
        type="button"
        role="menuitem"
        disabled={disabled}
        className="tw-block tw-w-full tw-whitespace-nowrap tw-border-0 tw-bg-transparent tw-px-3 tw-py-1.5 tw-text-left tw-text-[13px] enabled:hover:tw-bg-base-200 disabled:tw-cursor-default disabled:tw-opacity-50"
        onClick={() => {
          onClose()
          onEdit(activity)
        }}
      >
        {t('vehicleActivityEdit.menu')}
      </button>
    </div>,
    document.body,
  )
}

export default VehicleActivityContextMenu
